import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gql, useLazyQuery } from '@apollo/client';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TextField, Box, Button, Grid, Autocomplete } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../features/auth/authSlice';
import { IProject } from '../../../types/project';
import LoadingLogo from '../../common/LoadingLogo';
import { convertAllDates, camelToTitle, numToCurrency } from '../../../utils/Functions';
import StandardTable from '../../tables/StandardTable';
import { IDealDeposit } from '../../../types/CreateDealForm';
import { chequeTypes } from '../../../utils/Constants';

const Deposits = () => {
  const user = useSelector(selectUser);
  const [dateStart, setDateStart] = useState<Date | null>(new Date(new Date().setHours(0, 0, 0, 0)));
  const [dateEnd, setDateEnd] = useState<Date | null>(new Date());
  const [projects, setProjects] = useState<IProject[]>([]);
  const [deposits, setDeposits] = useState<IDealDeposit[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [count, setCount] = useState<number>(0);

  const [getDeposits, { loading }] = useLazyQuery(DEPOSITS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setDeposits(data.getDeposits.deposits);
      setCount(data.getDeposits.count);
    },
  });

  const columns = useMemo(() => {
    return [
      {
        Header: 'Project',
        accessor: (rowData: any) => rowData.deal.project.name,
      },
      {
        Header: 'Suite',
        accessor: (rowData: any) => {
          if (rowData.deal && rowData.deal.unit) {
            return (
              <Link
                style={{ textDecoration: 'none', color: 'inherit' }}
                rel="noopener noreferrer"
                target="_blank"
                to={`/${rowData.deal.project._id}/dashboard/${rowData.deal.unit._id}`}
              >
                <strong style={{ cursor: 'pointer' }}>{rowData.deal.unit.suite}</strong>
              </Link>
            );
          } else return '';
        },
      },
      {
        Header: 'Deposit',
        accessor: (rowData: any) => rowData.name,
      },
      {
        Header: 'Amount',
        accessor: (rowData: any) => numToCurrency.format(rowData.amount),
      },
      {
        Header: 'Status',
        accessor: (rowData: any) => (rowData.chequeType ? camelToTitle(rowData.chequeType) : 'Not Received'),
      },
      {
        Header: 'Due Date',
        accessor: (rowData: any) => convertAllDates(rowData.dueDate, 'PP'),
      },
    ];
  }, []);

  useEffect(() => {
    getDeposits({
      variables: {
        projects: projects.length ? projects.map((project: IProject) => project._id) : [],
        statuses,
        dateStart,
        dateEnd,
        page: pageNumber,
        perPage: 20,
      },
    });
  }, [pageNumber]);

  const handleDeposits = () => {
    getDeposits({
      variables: {
        projects: projects.length ? projects.map((project: IProject) => project._id) : [],
        statuses,
        dateStart,
        dateEnd,
        page: pageNumber,
        perPage: 20,
      },
    });
  };

  return (
    <Box>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              sx={{ height: '100%' }}
              multiple
              options={[user?.projectAccess!.map((projectAccess: any) => projectAccess?.project)].flat()}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              disableClearable={false}
              freeSolo={false}
              value={projects}
              onChange={(e, value) => {
                setProjects(value!);
              }}
              renderInput={(params) => <TextField {...params} label="Projects" size="medium" />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              sx={{ height: '100%' }}
              multiple
              options={[chequeTypes.map((chequeType: any) => chequeType.value)].flat()}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              disableClearable={false}
              freeSolo={false}
              value={statuses}
              onChange={(e, value) => {
                setStatuses(value!);
              }}
              renderInput={(params) => <TextField {...params} label="Deposit Statuses" size="medium" />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DateTimePicker
              label={'Start Due Date (YYYY/MM/DD)'}
              value={dateStart}
              onChange={(newValue) => {
                setDateStart(newValue);
              }}
              sx={{ width: '100%'}}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DateTimePicker
              label={'End Due Date (YYYY/MM/DD)'}
              value={dateEnd}
              onChange={(newValue) => {
                setDateEnd(newValue);
              }}
              sx={{ width: '100%'}}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button sx={{ height: '100%' }} variant="contained" color="primary" onClick={() => handleDeposits()}>
              Search
            </Button>
          </Grid>
        </Grid>
      </LocalizationProvider>
      {loading ? (
        <Box
          sx={{
            textAlign: 'center',
          }}
        >
          <LoadingLogo />
        </Box>
      ) : deposits.length ? (
        <Box>
          <Box>
            <StandardTable data={deposits} columns={columns} count={count} />
          </Box>
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button disabled={pageNumber === 1} onClick={() => setPageNumber(pageNumber - 1)}>
              {'<'}
            </Button>
            <span>{pageNumber}</span>
            <Button disabled={pageNumber === Math.ceil(count / 20)} onClick={() => setPageNumber(pageNumber + 1)}>
              {'>'}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center' }}>
          <em>No Deposits Found!</em>
        </Box>
      )}
    </Box>
  );
};

const DEPOSITS = gql`
  query getDeposits($projects: [MongoID], $statuses: [String], $dateStart: Date!, $dateEnd: Date!, $page: Float!, $perPage: Float!) {
    getDeposits(projects: $projects, statuses: $statuses, dateStart: $dateStart, dateEnd: $dateEnd, page: $page, perPage: $perPage) {
      deposits {
        _id
        name
        chequeType
        amount
        dueDate
        deal {
          project {
            _id
            name
          }
          unit {
            _id
            suite
          }
        }
      }
      count
    }
  }
`;

export default Deposits;
